import { useState, useEffect, useMemo, useCallback } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useFormik } from 'formik'

import { toast } from 'react-toastify'
import AddToRunsheetToast from '../AddToRunsheetToast'
import {
    ES_ENGINE_DOCUMENTS,
    ES_ENGINE_INDEX_DOCUMENTS,
    ES_INDEX_STRATEGY_INNER,
    ES_INDEX_SUFFIXES_BY_STRATEGY, PURCHASE_TYPE_VIEW_INDEX_DOCUMENT,
} from '@/constants'
import { useSearchParams } from 'react-router-dom'
import { parseDocumentFromElastic } from '@/utils/elasticsearch'
import { PENDO_EVENTS, pendoTrack } from '@/utils/pendo'
import useAbortableFetch from '@/hook/useAbortableFetch'
import useDocumentPopupHandler from '@hook/useDocumentPopupHandler'

export const TAB_SEARCH_DOCS_KEY = 'searchDocuments'
export const TAB_SEARCH_INDEX_KEY = 'searchIndexData'
export const TAB_BROWSE_KEY = 'browse'
export const TAB_ES_SEARCH_WILDCARD = 'esSearchWildcard'

// @todo not sure why this was unused
// export const TAB_ES_SEARCH_ADVANCED = 'esSearchAdvanced'

export const tabs = [
    // 2023-05-08
    // Hid the normal search for now since it's buggy, but don't delete
    // { key: TAB_SEARCH_DOCS_KEY, title: 'Search' },
    { key: TAB_SEARCH_INDEX_KEY, title: 'Advanced Search' },
    { key: TAB_BROWSE_KEY, title: 'Browse' },
]

// Maximum search results per page query
const searchResultsPerPage = 20

// Initial form values for document (runsheetDocumentValues) search
// this should not be used anymore as there's no runsheet search, only index
// const initialDocumentValues = {
//     name: '',
//     state: '',
//     county: '',
//     Full_Grantor: '',
//     Full_Grantee: '',
//     grantorGrantee: '',
//     volume: '',
//     page: '',
//     instrumentNumber: '',
//     instrumentType: '',
//     bookType: '',
//     fileDate: '',
//     instrumentDate: '',
//     part: '',
//     section: '',
//     block: '',
//     township: '',
//     survey: '',
//     abstract: '',
//     subdivision: '',
//     subdivisionLot: '',
//     subdivisionBlock: '',
//     minAcreage: '',
//     maxAcreage: '',
// }

// Initial form values for index search
const initialIndexValues = {
    grantor: '',
    grantee: '',
    grantorGrantee: '',
    county: '',
    documentNumber: '',
    bookType: '',
    bookVolume: '',
    bookPage: '',
    instrumentDateFrom: '',
    instrumentDateTo: '',
    fileDateFrom: '',
    fileDateTo: '',
    documentType: '',
    allLegalDescriptions: '',
}

/**
 * 
 * @param {UUID} collectionId
 * @param {string} routeActiveTab
 * @returns {CollectionViewModel}
 */
const useCollectionViewModel = ({
    collectionId,
    routeActiveTab,
}) => {
    
    const [searchParams, setSearchParams] = useSearchParams()
    
    // @todo this still needs a lot of cleanup
    
    const [loading, setLoading] = useState(true)
    const [documentName, setDocumentName] = useState('')
    const [documentInfo, setDocumentInfo] = useState({ id: '', name: '' })
    const [markedDocuments, setMarkedDocuments] = useState([])
    
    // Runsheets related
    const [runsheetDocs, setRunsheetDocs] = useState([])
    const [markedRunsheetDocs, setMarkedRunsheetDocs] = useState([])
    const [showAddToRunsheet, showHideAddToRunsheet] = useState(false)
    const [showSelectRunsheetTabs, showHideSelectRunsheetTabs] = useState(false)
    
    // Dialogs
    const [selectRunsheetDialogOpen, setSelectRunsheetDialogOpen] = useState(false)
    const [createRunsheetDialogOpen, setCreateRunsheetDialogOpen] = useState(false)
    
    // Also dialogs... these can be moved to the global store
    const [showDeleteDocument, setShowDeleteDocument] = useState(false)
    const [showEditDocument, setShowEditDocument] = useState(false)
    
    // Current list of cached documents (updated when search is run)
    const [documents, setDocuments] = useWireState(store.documents)
    
    const [currentCollection, setCurrentCollection] = useWireState(store.currentCollection)
    
    // Query for full-text/wildcard search
    const [documentSearchQuery, setDocumentSearchQuery] = useWireState(store.documentSearchQuery, searchParams.get('q'))
    
    const allRunsheets = useWireValue(store.runsheets)
    const [selectedRunsheet, setSelectedRunsheet] = useWireState(store.currentRunsheet)
    
    // Form values, set onSubmit, which automatically update the search payload
    const [values, setValues] = useState({})
    
    // const [errorShow, setErrorShow] = useState(false)
    // const [showErrorPopup, setShowErrorPopup] = useState(false)
    
    const [noSearchResultsVisible, setNoSearchResultsVisible] = useState(false)
    
    // Current page index for pagination
    const [pageIndex, setPageIndex] = useState(0)
    
    // Loading state for adding documents to a runsheet
    const [isAddingToRunsheet, setIsAddingToRunsheet] = useState(false)
    
    // Loading state for removing documents from a runsheet
    const [isRemovingFromRunsheet, setIsRemovingFromRunsheet] = useState(false)
    
    const remoteConfig = useWireValue(store.remoteConfig)
    
    // Currently selected tab (search, browse, etc.)
    const activeTab = useMemo(() => routeActiveTab ?? TAB_SEARCH_DOCS_KEY, [routeActiveTab])
    
    const isParseCollection = useMemo(() => currentCollection?.isParse, [currentCollection])
    
    const engine = useMemo(() => (isParseCollection ? ES_ENGINE_INDEX_DOCUMENTS : ES_ENGINE_DOCUMENTS) +
            ES_INDEX_SUFFIXES_BY_STRATEGY[ES_INDEX_STRATEGY_INNER], [isParseCollection])
    
    const sortField = useMemo(() => searchParams.get('field'), [searchParams.get('field')])
    const sortOrder = useMemo(() => searchParams.get('order'), [searchParams.get('order')])
    
    // Filters tabs to prevent Index Search to show for tabs that are not from parse
    // @todo somewhere in the future user tabs could have Index Search so this might change
    // hopefully by then we merged the search all together into one
    const filteredTabs = useMemo(() => {
        
        const copyTabs = [...tabs]
        
        if (remoteConfig?.elasticSearch?.wildcardSearchEnabled)
            copyTabs.unshift({ key: TAB_ES_SEARCH_WILDCARD, title: 'Full-Text Search' })
        
        return copyTabs
        
    }, [remoteConfig])
    
    // Search payload, automatically sent when changed
    const payload = useMemo(() => ({
        collectionId,
        query: values,
        offset: (pageIndex * searchResultsPerPage),
        limit: searchResultsPerPage,
        engine,
        sort: { field: sortField, order: sortOrder },
    }), [collectionId, values, pageIndex, engine, sortField, sortOrder])
    
    // changing sorting resets the page too
    useEffect(() => {
        setPageIndex(0)
    }, [
        sortField,
        sortOrder,
    ])
    
    const searchDocuments = useCallback(async () => {
        
        // Don't search unless we have the current collection set
        if (!currentCollection || activeTab === TAB_BROWSE_KEY)
            return
        
        setDocuments({})
        setMarkedDocuments([])
        setMarkedRunsheetDocs([])
        setLoading(true)
        setNoSearchResultsVisible(false)
        
        if (activeTab === TAB_SEARCH_INDEX_KEY) {
            payload.search_after = payload.offset > 0 ? documents.search_after : []
            const data = await actions.advancedSearchElastic(payload) //searchRunsheetIndex(payload)
            
            data.rows = parseDocumentFromElastic(data.rows)
            setDocuments(data)
        } else if (activeTab === TAB_SEARCH_DOCS_KEY)
            await actions.searchRunsheetDocument(payload)
        
        setLoading(false)
        
    }, [payload, activeTab, documents])
    
    const { handleSubmit, handleChange, handleReset } = useFormik({
        initialValues: initialIndexValues,
        onSubmit: formValues => {
            setValues({ ...formValues })
            setPageIndex(0)
        },
    })
    
    const handleFormReset = e => {
        
        handleReset(e)
        
        // @todo there is a better way to handle this
        // eslint-disable-next-line no-restricted-globals
        const form = document.searchForm
        
        // had to manually set the values otherwise they wouldn't disappear on the screen
        // as the inputs are uncontrolled
        // also, form.reset didn't work for this
        Object.keys(initialIndexValues).forEach(k => {
            if (form.elements[k]) {
                form.elements[k].value = initialIndexValues[k]
                
                // flatpickr wouldn't allow me just to reset the value, so had to force it :)
                if (form.elements[k]._flatpickr)
                    form.elements[k]._flatpickr.setDate(initialIndexValues[k])
            }
        })
    }
    
    const handleKeyUp = useCallback(e => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        e.stopPropagation()
        return handleSubmit(e)
    }, [handleSubmit])
    
    useEffect(() => {
        
        // Auto update the query in the URL, so it
        // doesn't disappear when the page is reloaded
        setSearchParams(prev => {
            prev.set('q', documentSearchQuery ?? prev.get('q') ?? '')
            return prev
        })
        
    }, [documentSearchQuery])
    
    useAbortableFetch(() => searchDocuments(), [payload, activeTab])
    
    useEffect(() => {
        
        if (collectionId === currentCollection?.id)
            return
        
        if (currentCollection?.id !== collectionId)
            setCurrentCollection(null)
        
        actions.fetchCollectionById(collectionId)
        actions.fetchRunsheets()
        
        
        
    }, [collectionId, currentCollection])
    
    useEffect(() => {
        return () => {
            setCurrentCollection(null)
        }
    }, [])
    
    useEffect(() => {
        
        if (selectedRunsheet)
            setRunsheetDocs(selectedRunsheet.rdj ?? [])
        
    }, [selectedRunsheet])
    
    const { payments } = useDocumentPopupHandler()
    
    const addDocumentsToRunsheet = useCallback(async (runsheetId, documentIds, remainingBalance, fundingSource) => {
        
        const filteredIds = documentIds.filter(id => !selectedRunsheet.rdj.some(r => r.documentId === id))
        
        filteredIds.forEach(documentId => pendoTrack(PENDO_EVENTS.ADD_TO_RUNSHEET, {
            runsheetId: selectedRunsheet.id,
            documentId,
        }))
        
        try {
            
            const insertedDocIds = await actions.addRunsheetDocument(
                fundingSource, { runsheetId, documentIds })
            
            if (filteredIds.length !== documentIds.length)
                toast.warn(`${documentIds.length - filteredIds.length} of the ` +
                    'selected documents are already present in this runsheet.')
            
            if (filteredIds.length !== insertedDocIds.length)
                toast.warn(`${filteredIds.length - insertedDocIds.length} of the ` +
                    'selected documents couldn\'t be inserted in this runsheet.')
            
            if (insertedDocIds.length > 0)
                toast.success(
                    <AddToRunsheetToast
                        documentIds={insertedDocIds}
                        runsheetIds={[runsheetId]} />)
            
        } catch (e) {
            
            toast.error('Failed to insert selected documents in this runsheet.')
            
        }
        
        if (!remoteConfig?.realtimeEnabled) await actions.updateLocalCredits()
        setMarkedRunsheetDocs([])
        setMarkedDocuments([])
        
    }, [selectedRunsheet])
    
    const removeRunsheetDocument = async () => {
        
        setIsRemovingFromRunsheet(true)
        
        try {
            
            const checkDocIds = [...markedRunsheetDocs]
            
            await actions.deleteDocumentFromRunsheet(selectedRunsheet.id, checkDocIds)
            await actions.fetchRunsheetById(selectedRunsheet.id)
            setMarkedRunsheetDocs([])
            
            toast.success(`Successfully removed ${checkDocIds.length}` +
                `${checkDocIds.length === 1 ? ' document' : ' documents'} from this runsheet`)
            
        } finally {
            
            setIsRemovingFromRunsheet(false)
            
        }
        
    }
    
    const onAddToRunsheetClick = async selectedTabs => {
        
        setIsAddingToRunsheet(true)
        
        try {
            
            // If a runsheet is already selected & one or more documents are selected,
            // automatically add the documents to that runsheet; otherwise show the runsheets dialog
            if (!selectedRunsheet?.id?.length || !markedDocuments?.length)
                return
            
            // Fetch the full records, so we have the collection & related metadata
            const records = remoteConfig?.payments?.enabled
                ? await actions.checkDocumentStatuses(
                    markedDocuments,
                    markedDocuments.map(it => ({ id: it })))
                : markedDocuments
            
            const documentIds = [...markedDocuments]
            
            if (remoteConfig?.payments?.enabled) {
                
                const paymentRequiredRecords = records
                    .filter(it => payments.shouldShowPurchaseRecordDialog(it, PURCHASE_TYPE_VIEW_INDEX_DOCUMENT, 1))
                
                if (paymentRequiredRecords.length)
                    return payments.showPurchaseDialog({
                        purchaseType: PURCHASE_TYPE_VIEW_INDEX_DOCUMENT,
                        itemCount: paymentRequiredRecords.length,
                        onConfirmPayment: 
                            async (remainingBalance, fundingSource) => {
                                // First add to main tab using addDocumentsToRunsheet
                                await addDocumentsToRunsheet(
                                    selectedRunsheet.id, 
                                    documentIds, 
                                    remainingBalance, 
                                    fundingSource,
                                )
                                
                                // Then add to additional tabs using addDocumentValuesToRunsheetTab
                                if (selectedTabs) {
                                    for (const tabId of selectedTabs) {
                                        if (tabId !== 0) { // Skip main tab as it's already added
                                            await actions.addDocumentValuesToRunsheetTab(selectedRunsheet.id, 
                                                tabId, documentIds)
                                        }
                                    }
                                    
                                    // Refresh the runsheet data
                                    const updatedRunsheet = await actions.fetchRunsheetById(selectedRunsheet.id)
                                    
                                    setSelectedRunsheet(updatedRunsheet)
                                }
                            },
                    })
            }
            
            // First add to main tab using addDocumentsToRunsheet
            await addDocumentsToRunsheet(selectedRunsheet.id, documentIds)
            
            // Then add to additional tabs using addDocumentValuesToRunsheetTab
            if (selectedTabs) {  
                for (const tabId of selectedTabs) {
                    if (tabId !== 0) { // Skip main tab as it's already added
                        await actions.addDocumentValuesToRunsheetTab(selectedRunsheet.id, tabId, documentIds)
                    }
                }
                
                // Refresh the runsheet data
                const updatedRunsheet = await actions.fetchRunsheetById(selectedRunsheet.id)
                
                setSelectedRunsheet(updatedRunsheet)
            }
            
            /* const filteredIds = documentIds.filter(id => !selectedRunsheet.rdj.some(r => r.documentId === id))
            
            filteredIds.forEach(documentId => pendoTrack(PENDO_EVENTS.ADD_TO_RUNSHEET, {
                runsheetId: selectedRunsheet.id,
                documentId,
            }))
            
            try {
                
                const insertedDocIds = await actions.addRunsheetDocument({ runsheetIds, documentIds })
                
                if (filteredIds.length !== documentIds.length) {
                    toast.warn(`${documentIds.length - filteredIds.length} of the ` +
                        'selected documents are already present in this runsheet.')
                }
                
                if (filteredIds.length !== insertedDocIds.length) {
                    toast.warn(`${filteredIds.length - insertedDocIds.length} of the ` +
                        'selected documents couldn\'t be inserted in this runsheet.')
                }
                
                if (insertedDocIds.length > 0) {
                    toast.success(<AddToRunsheetToast documentIds={insertedDocIds} runsheetIds={runsheetIds} />)
                }
                
            } catch (e) {
                
                toast.error('Failed to insert selected documents in this runsheet.')
                
            }
            
            setMarkedRunsheetDocs([])
            setMarkedDocuments([]) */
            
        } finally {
            
            setIsAddingToRunsheet(false)
            
        }
        
    }
    
    return {
        searchParams,
        setSearchParams,
        tabs: filteredTabs,
        searchResultsPerPage,
        loading,
        setLoading,
        currentCollection,
        allRunsheets,
        documents,
        selectRunsheetDialogOpen,
        setSelectRunsheetDialogOpen,
        selectedRunsheet,
        setSelectedRunsheet,
        markedDocuments,
        setMarkedDocuments,
        documentSearchQuery,
        setDocumentSearchQuery,
        runsheetDocs,
        setRunsheetDocs,
        markedRunsheetDocs,
        setMarkedRunsheetDocs,
        createRunsheetDialogOpen,
        setCreateRunsheetDialogOpen,
        showDeleteDocument,
        setShowDeleteDocument,
        showEditDocument,
        setShowEditDocument,
        documentName,
        setDocumentName,
        documentInfo,
        setDocumentInfo,
        showAddToRunsheet,
        showHideAddToRunsheet,
        noSearchResultsVisible,
        activeTab,
        showHideSelectRunsheetTabs,
        showSelectRunsheetTabs,
        
        sortField,
        sortOrder,
        
        // errorShow,
        // setErrorShow,
        handleKeyUp,
        handleChange,
        removeRunsheetDocument,
        onAddToRunsheetClick,
        handleSubmit,
        handleFormReset,
        pageIndex,
        setPageIndex,
        isAddingToRunsheet,
        setIsAddingToRunsheet,
        isRemovingFromRunsheet,
        setIsRemovingFromRunsheet,
    }
    
}

export default useCollectionViewModel
